import { initializeApp } from "firebase/app";
import { initializeFirestore } from "firebase/firestore";
// firebase/messaging, firebase/auth 는 로그인 후에만 필요 → 동적 import 로 초기 번들에서 분리

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
};

// #476: Safari 만 겪는 WebChannel/CORP 문제인데 이전엔 모든 브라우저에 강제 적용돼 있었음.
// 크롬/엣지 등은 정상 WebChannel(네이티브 웹소켓)을 쓰는 게 초기 연결·첫 스냅샷 수신이
// 훨씬 빠르고 안정적 — long-polling 은 그만큼 실시간 카운트 등이 "구독 완료 전 값"을
// 더 오래 보여줄 여지를 만든다. Safari 에만 스코프.
const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent || "");

const app = initializeApp(firebaseConfig);
export const db = initializeFirestore(app, {
  // Safari 가 WebChannel 응답에 CORP 헤더 위반으로 Listen 채널 차단 → 강제 long-polling + fetch stream 비활성
  ...(isSafari ? { experimentalForceLongPolling: true, useFetchStreams: false } : {}),
} as Parameters<typeof initializeFirestore>[1]);
// auth 는 동적 로드 (초기 번들 분리). 최초 사용 시 firebase/auth import + getAuth 캐시.
let _auth: any = null;
async function getAuthInstance() {
  if (!_auth) {
    const { getAuth } = await import("firebase/auth");
    _auth = getAuth(app);
  }
  return _auth;
}

const API_BASE_AUTH = process.env.REACT_APP_API_URL || "https://api.trippang.com";
let _firestoreAuthPromise: Promise<boolean> | null = null;
let _fcmOnMessageUnsub: (() => void) | null = null;

/** Firestore 실시간 구독을 위한 Firebase Auth (Custom Token).
 *  - 백엔드 /user/session/firestore-token → custom token → signInWithCustomToken
 *  - 결과: request.auth.uid = user_id 문자열 (Rules 매칭) */
export async function ensureFirestoreAuth(): Promise<boolean> {
  const auth = await getAuthInstance();
  const { signInWithCustomToken, signOut } = await import("firebase/auth");
  const myId = localStorage.getItem("user_id");
  if (auth.currentUser && myId && auth.currentUser.uid === myId) return true;
  if (auth.currentUser && (!myId || auth.currentUser.uid !== myId)) {
    try { await signOut(auth); } catch { /* defensive */ }
  }
  if (_firestoreAuthPromise) return _firestoreAuthPromise;
  _firestoreAuthPromise = (async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return false;
      const res = await fetch(`${API_BASE_AUTH}/user/session/firestore-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      const data = await res.json();
      const ft = data?.data?.firestore_token;
      if (!ft) return false;
      await signInWithCustomToken(auth, ft);
      return true;
    } catch {
      return false;
    } finally {
      _firestoreAuthPromise = null;
    }
  })();
  return _firestoreAuthPromise;
}

export async function signOutFirestore(): Promise<void> {
  try {
    const auth = await getAuthInstance();
    const { signOut } = await import("firebase/auth");
    await signOut(auth);
  } catch { /* silent */ }
}

// FCM Messaging (브라우저 지원 시에만) — 동적 import 로 초기 번들 분리
let messaging: any = null;

export async function initMessaging() {
  if (messaging) return messaging;
  const { getMessaging, isSupported } = await import("firebase/messaging");
  const supported = await isSupported();
  if (!supported) return null;
  messaging = getMessaging(app);
  return messaging;
}

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";
const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";

/**
 * FCM 토큰 발급 + 서버 등록
 * - Notification 권한 요청
 * - 토큰 발급 후 /user/fcm/register API 호출
 */
export async function registerFcmToken(): Promise<string | null> {
  try {
    if (typeof Notification === "undefined") return null;
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return null;

    const msg = await initMessaging();
    if (!msg || !VAPID_KEY) return null;
    const { getToken, onMessage } = await import("firebase/messaging");

    // Service Worker 등록 (PWA + FCM 통합) — 항상 최신 버전 강제 적용
    const sw = await navigator.serviceWorker.register("/service-worker.js", { updateViaCache: "none" });
    await sw.update();

    // 우리 SW 외 모든 SW 강제 unregister — 과거에 등록됐던 firebase-messaging-sw.js 등이
    // active 상태로 남아있으면 같은 push 이벤트를 둘 다 처리하여 알림/윈도우가 다중으로 뜸.
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      const ourScope = sw.scope;
      await Promise.all(
        regs
          .filter((r) => r.scope !== ourScope || (r.active && !r.active.scriptURL.endsWith("/service-worker.js")))
          .map((r) => r.unregister().catch(() => false))
      );
    } catch { /* defensive */ }

    const token = await getToken(msg, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: sw,
    });
    if (!token) return null;

    // 서버에 토큰 등록
    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      await fetch(`${API_BASE}/user/fcm/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          token,
          device_type: "web",
          device_name: navigator.userAgent.substring(0, 100),
        }),
      });
    }

    // 포그라운드 메시지 핸들러 — OS 알림 + 인앱 알림 목록 (data-only 메시지 기준).
    // registerFcmToken 이 여러 번 호출되더라도 핸들러 중복 등록 방지.
    if (_fcmOnMessageUnsub) { _fcmOnMessageUnsub(); _fcmOnMessageUnsub = null; }
    _fcmOnMessageUnsub = onMessage(msg, (payload) => {
      // 본인이 보낸 메시지는 무시 (송신자 ID 비교)
      const senderId = payload.data?.sender_id;
      const myId = localStorage.getItem("user_id");
      if (senderId && myId && String(senderId) === String(myId)) return;

      // 제목/본문은 data 안 (notification payload 미사용)
      const title = payload.data?.title || payload.notification?.title || "TRIPPANG";
      const body = payload.data?.body || payload.notification?.body || "";
      if (title || body) {
        // service-worker.js 의 push 핸들러와 동일하게 data(+tag) 를 반드시 실어야
        // notificationclick 에서 올바른 화면(베너 상세/팝업)으로 라우팅하고, 종 뱃지
        // 읽음 처리(targetId 매칭)도 정상 동작한다. 예전엔 이 포그라운드 경로만 data 없이
        // showNotification 을 호출해서 — 앱이 켜져있는 상태로 받은 알림을 클릭하면
        // notificationclick 이 아무 목적지도 못 찾고 그냥 홈으로 갔었음.
        const data = payload.data || {};
        const tag = data.target_id ? `trippang-banner-${data.target_id}` : "trippang-fcm";
        // 서비스워커가 페이지를 컨트롤 중(PWA standalone 등)이면 new Notification() 이
        // "Illegal constructor" 로 실패함 — 이 경우 SW의 showNotification() 을 써야 함.
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.ready
            .then((reg) => reg.showNotification(title, { body, icon: "/user_192.png", data, tag, renotify: true }))
            .catch(() => {});
        } else {
          try {
            new Notification(title, { body, icon: "/user_192.png", data } as NotificationOptions);
          } catch {}
        }
      }
      // 인앱 알림 목록에 추가
      const { dispatchFcmEvent } = require("./contexts/NotificationContext");
      dispatchFcmEvent({
        type: payload.data?.type || "",
        boardType: payload.data?.board_type || "",
        targetId: payload.data?.target_id || "",
        nickName: payload.data?.nick_name || "",
        source: payload.data?.source || "",
        title,
        body,
        postTitle: payload.data?.post_title || "",
      });
    });

    // 등록된 SW에 user_id 전달 (백그라운드 메시지 필터용)
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      const userId = localStorage.getItem("user_id");
      if (userId) {
        navigator.serviceWorker.controller.postMessage({ type: "USER_ID", userId });
      }
    }

    localStorage.setItem("fcm_token", token);
    return token;
  } catch (e) {
    console.warn("FCM registration failed:", e);
    return null;
  }
}

/**
 * 로그아웃 시 FCM 토큰 비활성화
 */
export async function unregisterFcmToken(): Promise<void> {
  try {
    const token = localStorage.getItem("fcm_token");
    const accessToken = localStorage.getItem("access_token");
    if (token && accessToken) {
      await fetch(`${API_BASE}/user/fcm/unregister`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ token }),
      });
    }
    localStorage.removeItem("fcm_token");
  } catch (e) {
    console.warn("FCM unregister failed:", e);
  }
}
